import React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { z } from "zod";
import { BROKEN_IMAGE_SRC, UNKNOWN_PERSON_NAME } from "@remi/constants";
import { splitContactName } from "@remi/formatting";
import { RnUserEmpty } from "@remi/icons/next";

import { cn } from "../lib/utils";
import { RemiTooltip, RemiTooltipContent, RemiTooltipTrigger } from "./RemiTooltip";

const AvatarStatusValues = ["active", "inactive", "unresponsive"] as const;
const AvatarStatusSchema = z.enum(AvatarStatusValues);
type AvatarStatus = z.infer<typeof AvatarStatusSchema>;

interface AvatarUser {
  contact: { name: string };
}

type GroupAvatarUser = AvatarUser & {
  id?: string;
  url?: string;
  icon?: React.ReactNode;
  status?: AvatarStatus;
};

const avatarVariants = cva("flex h-auto w-auto shrink-0 font-medium text-white", {
  variants: {
    variant: {
      default: "",
    },
    size: {
      xxs: "h-3 min-h-3 w-3 min-w-3 font-medium text-[5px] leading-[5px] tracking-tight",
      xs: "h-4 min-h-4 w-4 min-w-4 font-medium text-[6px] leading-[6px]",
      sm: "h-[18px] min-h-[18px] w-[18px] min-w-[18px] font-medium text-[7px] leading-[7px]",
      md: "h-6 min-h-6 w-6 min-w-6 font-medium text-[10px] leading-[10px]",
      lg: "h-7 min-h-7 w-7 min-w-7 font-medium text-[12px] leading-[12px]",
      xlg: "h-9 min-h-9 w-9 min-w-9 font-medium text-[14px] leading-[14px]",
      xl: "h-16 min-h-16 w-16 min-w-16 font-medium text-[30px] leading-[30px]",
      xxl: "h-[128px] min-h-[128px] w-[128px] min-w-[128px] font-medium text-[62px] leading-[62px]",
    },
    status: {
      active: "rounded-full bg-border-accent p-px",
      inactive: "rounded-full bg-border-primary p-px",
      unresponsive: "rounded-full bg-icon-orange p-px",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "sm",
  },
});

type RemiAvatarProps<T extends AvatarUser> = React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants> & {
    person?: T;
    url?: string;
    size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xlg" | "xl" | "xxl";
    border?: boolean;
    icon?: React.ReactNode;
    status?: AvatarStatus;
  };

const RemiAvatar = <T extends AvatarUser>({
  className,
  person,
  size = "sm",
  border = true,
  icon,
  url,
  status,
  ...props
}: RemiAvatarProps<T>) => {
  if (!person) {
    return (
      <div className={cn(avatarVariants({ size, status }), "flex items-center justify-center")}>
        <RnUserEmpty className="text-icon-secondary" size={size === "xlg" ? "lg" : size} />
      </div>
    );
  }
  return (
    <AvatarPrimitive.Root className={cn(avatarVariants({ size, status }), className)} {...props}>
      <div className="relative flex h-full w-full items-center justify-center">
        {icon && (
          <div className="absolute right-[-1px] bottom-[-1px] z-10 flex items-center justify-center rounded-full text-icon-secondary">
            {icon}
          </div>
        )}
        {url && url !== BROKEN_IMAGE_SRC && (
          <RemiAvatarImage
            className={cn(border && "border border-border-tertiary", "bg-background-primary")}
            src={url}
            alt={person.contact.name}
          />
        )}
        <RemiAvatarFallback
          className={cn(border && "border border-border-tertiary")}
          person={person}
          delayMs={url && url !== BROKEN_IMAGE_SRC ? 600 : undefined}
        />
      </div>
    </AvatarPrimitive.Root>
  );
};

type RemiAvatarImageProps = React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>;

const RemiAvatarImage = ({ className, ...props }: RemiAvatarImageProps) => {
  return <AvatarPrimitive.Image className={cn("h-full w-full rounded-full object-cover", className)} {...props} />;
};

type RemiAvatarFallbackProps<T extends AvatarUser> = React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & {
  person: T;
};

const RemiAvatarFallback = <T extends AvatarUser>({ className, person, ...props }: RemiAvatarFallbackProps<T>) => {
  const [firstName, lastName] = splitContactName(person.contact.name);
  const initials =
    lastName !== UNKNOWN_PERSON_NAME
      ? `${firstName.length > 0 ? firstName[0] : ""}${lastName.length > 0 ? lastName[0] : ""}`
      : "?";

  return (
    <AvatarPrimitive.Fallback
      className={cn(
        "flex h-full w-full cursor-pointer items-center justify-center rounded-full",
        pickRandomColor(person.contact.name),
        className,
      )}
      {...props}
    >
      {initials}
    </AvatarPrimitive.Fallback>
  );
};

type RemiOverflowAvatarProps<T extends GroupAvatarUser> = React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants> & {
    persons: T[];
    size: "xs" | "sm" | "md" | "lg" | "xlg" | "xl" | "xxl";
    border?: boolean;
  };

const RemiOverflowAvatar = <T extends GroupAvatarUser>({
  className,
  persons,
  size,
  border,
}: RemiOverflowAvatarProps<T>) => {
  return (
    <RemiTooltip>
      <RemiTooltipTrigger asChild>
        <RemiAvatar
          size={size}
          person={{ contact: { name: `+ ${persons.length}` } }}
          status={persons.some((p) => p.status === "active") ? "active" : undefined}
          className={cn(className)}
          border={border}
        />
      </RemiTooltipTrigger>
      <RemiTooltipContent>
        <div className="flex flex-col gap-1">
          {persons.map((person, index) => (
            <div className="flex flex-row items-center gap-1" key={index}>
              <RemiAvatar
                className="mr-1"
                key={index}
                person={person}
                size="sm"
                border={border}
                status={person.status}
                icon={person.icon}
              />
              <div className="flex flex-row items-center gap-1.5 text-xs">
                <span className="font-medium">{person.contact.name}</span>
              </div>
            </div>
          ))}
        </div>
      </RemiTooltipContent>
    </RemiTooltip>
  );
};

type RemiAvatarGroupProps<T extends GroupAvatarUser> = React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants> & {
    persons: T[];
    size?: "xs" | "sm" | "md" | "lg" | "xlg" | "xl" | "xxl";
    border?: boolean;
    overflow?: number;
  };

export const RemiAvatarGroup = <T extends GroupAvatarUser>({
  className,
  persons,
  size = "sm",
  border = true,
  overflow = 4,
  ...props
}: RemiAvatarGroupProps<T>) => {
  const skewMap = {
    xs: 6,
    sm: 7,
    md: 10,
    lg: 12,
    xl: 30,
    xlg: 36,
    xxl: 62,
  };

  return (
    <div className={cn("flex", className)} {...props}>
      {persons.map(
        (person, i) =>
          (i < overflow - 1 || overflow === persons.length) && (
            <div
              style={i !== 0 ? { marginLeft: `-${skewMap[size]}px` } : undefined}
              key={person.id ?? `${person.contact.name}-${i}`}
            >
              <RemiAvatar
                size={size}
                person={person}
                status={person.status}
                icon={person.icon}
                border={border}
                url={person.url}
              />
            </div>
          ),
      )}
      {persons.length > overflow && (
        <div style={{ marginLeft: `-${skewMap[size]}px` }}>
          <RemiOverflowAvatar persons={persons.slice(overflow - 1)} size={size} border={border} />
        </div>
      )}
    </div>
  );
};

const hashStringToNumber = (str: string, arrayLength: number): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash + str.charCodeAt(i)) % arrayLength;
  }
  return hash;
};

const pickRandomColor = (name: string): string => {
  const colors = [
    "bg-avatar-yellow",
    "bg-avatar-pink",
    "bg-avatar-green",
    "bg-avatar-teal",
    "bg-avatar-purple",
    "bg-avatar-orange",
  ];
  const colorIndex = hashStringToNumber(name, colors.length);
  if (name === "Anne Margaret Hanopol") {
    return "bg-avatar-pink";
  }
  if (name.includes("+")) {
    return "bg-background-accent text-text-faint";
  }
  return colors[colorIndex] || "";
};

export { RemiAvatar };
